import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { normalizeCouponCode, removeNonNumericCharacters } from '../utils/util';
import { CouponCodeDocument, GQLCouponCodeQuery, GQLCouponCodeQueryVariables, GQLTestPlace, useCouponCodeQuery } from '../generated/graphql';
import { PartialRequired } from '../utils/type-utils';
import { styled } from 'styled-components';
import { KYOFUKU_MEMBER_TYPE, CHUEI_BANK_MEMBER_TYPE, NavigateProps, TestPlacesProps } from '../entry-points/entry';
import { valid, useValidateState, toHalfString, isValidDate, bindStateToText } from '../utils/entry-utils';
import { RadioContainer, Radio, Text} from './entry-form-parts';
import { useApolloClient } from '@apollo/client';

export function InputKyofukuInformation({ onNext, onPrev, kyofukuMemberTypeState, kyofukuMemberIdState, kyofukuMemberNameState }) {
	return <>
		<h3>神奈川県教育福祉振興会会員情報の入力</h3>
		<p className="entry-form-text">会員の方が同行しない場合（同行者のみで）は振興会枠での検査は受けられませんので、ご注意ください。</p>
		<RadioContainer label="会員種別">
			<Radio state={kyofukuMemberTypeState} value={KYOFUKU_MEMBER_TYPE.CURRENT} name="kyofuku-member-type">現職会員① 〜60歳（60歳の誕生日の属する年度まで）</Radio>
			<Radio state={kyofukuMemberTypeState} value={KYOFUKU_MEMBER_TYPE.CURRENT2} name="kyofuku-member-type">現職会員② 61歳〜（61歳の誕生日の属する年度以降）</Radio>
			<Radio state={kyofukuMemberTypeState} value={KYOFUKU_MEMBER_TYPE.CONTINUATION} name="kyofuku-member-type">継続会員</Radio>
			<Radio state={kyofukuMemberTypeState} value={KYOFUKU_MEMBER_TYPE.COMPANION} name="kyofuku-member-type">会員の同行者</Radio>
		</RadioContainer>
		<Text state={kyofukuMemberIdState} label="会員番号*" name="kyofuku-member-id" />
		<p className="entry-form-text"><small className="note">会員同行者の場合は同行する会員様の会員番号をご入力ください</small></p>
		{/*--
        <Text state={kyofukuMemberNameState} label="会員氏名" />
        <p className="entry-form-text"><small className="note">会員同行者の場合は同行する会員様の氏名をご入力ください</small></p>
        --*/}
		<div className="button-container">
			<button className="prev-button" onClick={() => {
				onPrev();
			}}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" disabled={!valid(kyofukuMemberTypeState, kyofukuMemberIdState, kyofukuMemberNameState)} onClick={() => {
				onNext();
			}}>次へ</button>
		</div>
	</>;
}
export function InputChueiBankInformation({ onNext, onPrev, chueiBankMemberTypeState }) {
	return <>
		<h3>中栄信用金庫「医療健康サービスご利用の方」</h3>
		<p className="entry-form-text">具体的には「ねんきん友の会」会員様、「職域サポート」会員でデジタル会員証の発行を受けている方は、奉野市幸町の「川口医院」及び伊勢原市桜台の「うどファミリークリニック」、同市高森の「MBクリニック」でのMB検査（スタンダードフリーコース）を通常16,500円<small>（税込）</small>のところ、14,300円<small>（税込）</small>で受けていただけます。</p>
		<RadioContainer label="会員のタイプ">
			<Radio state={chueiBankMemberTypeState} value={CHUEI_BANK_MEMBER_TYPE.NENKIN_MEMBER} name="chuei-bank-member-type">「ねんきん友の会」会員の方</Radio>
			<Radio state={chueiBankMemberTypeState} value={CHUEI_BANK_MEMBER_TYPE.SYOKUIKI_MEMBER} name="chuei-bank-member-type">「職域サポート」会員でデジタル会員証をお持ちの方</Radio>
		</RadioContainer>
		<div className="button-container">
			<button className="prev-button" onClick={() => onPrev()}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" disabled={!valid(chueiBankMemberTypeState)} onClick={() => onNext()}>次へ</button>
		</div>
	</>;
}
export function InputRIEBEInformation({ onNext, onPrev, riebeUnionNameState }) {
	return <>
		<h3>一般社団法人企業福祉・共済総合研究所（RIEBE）会員関係者の方</h3>
		<p className="entry-form-text">一般社団法人企業福祉・共済総合研究所（RIEBE）会員関係者の方は、MB検査（スタンダードフリーコース）を通常16,500円（税込）のところ、特別料金14,300円（税込）にて受けていただけます。この企画は対象となるクリニック・病院が限定されていますので、ご注意ください</p>
		<Text state={riebeUnionNameState} label="会員名称（○○○健康保険組合、○○○共済会など）をご入力ください" name="riebe-union-name" />
		<div className="button-container">
			<button className="prev-button" onClick={() => onPrev()}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" disabled={!valid(riebeUnionNameState)} onClick={() => onNext()}>次へ</button>
		</div>
	</>;
}
const HorizontalInputContainer = styled.div`
	display: flex;
	gap: 2rem;
	.text-label {
		flex: 1 1 0;
		min-width: 0;
	}
`;
export function InputSapporoChushoInformation({ onNext, onPrev, sapporoChushoMemberIdState, sapporoChushoMemberNameState }) {
	const firstTextState = useValidateState('', v => /^\d{6}$/.test(v));
	const secondTextState = useValidateState('', v => /^\d{8}$/.test(v));
	useEffect(() => {
		const firstText = firstTextState[0];
		if (firstText.length > 5) {
			const secondTextEl = document.querySelector<HTMLInputElement>('input[name=second_text]');
			if (secondTextEl) {
				secondTextEl.focus();
			}
		}
	}, [firstTextState[0]]);
	useEffect(() => {
		const [firstText] = firstTextState;
		const [secondText] = secondTextState;
		sapporoChushoMemberIdState[1](`${firstText}-${secondText}`);
	}, [firstTextState[0], secondTextState[0]]);
	return <>
		<h3>公財 札幌市中小企業共済センター（さぽーとさっぽろ）会員の方</h3>
		<p className="entry-form-text">
		さぽーとさっぽろ会員の方は、以下の特別価格で「がんリスク検査」と「認知リスク検査」を受けられます。<br /></p>
		<p className="entry-form-text">● がんリスク検査 または 認知リスク検査<br />
		通常 19,800円（税込） → 特別価格 15,400円（税込）</p>
		<p className="entry-form-text">● がんリスク検査＋認知リスク検査（同時申込）<br />
		通常合計 24,200円（税込） → 特別価格 19,800円（税込）</p>
		<p className="entry-form-text">※検査を受けられるクリニック・病院は限られていますので、ご注意ください。</p>
		<HorizontalInputContainer>
			<Text state={firstTextState} label="企業番号（6桁）" placeholder='000000' name='first_text' maxLength={6} replacer={removeNonNumericCharacters} />
			<Text state={secondTextState} label="会員番号（8桁）" placeholder='00000000' name='second_text' maxLength={8} replacer={removeNonNumericCharacters} />
		</HorizontalInputContainer>
		<Text state={sapporoChushoMemberNameState} label="会員氏名 ※会員特典は、会員ご本人のみが対象となります。​" name="riebe-union-name" />
		<div className="button-container">
			<button className="prev-button" onClick={() => onPrev()}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" disabled={!valid(sapporoChushoMemberIdState, sapporoChushoMemberNameState)} onClick={() => onNext()}>次へ</button>
		</div>
	</>;
}
export function InputBohseiInformation({ onNext, onPrev }) {
	const passwordState = useValidateState('', v => v === '8966');
	return <>
		<h3>株式会社望星薬局社員情報の入力</h3>
		<Text state={passwordState} label="パスワード​" name="bohsei-password" />
		<div className="button-container">
			<button className="prev-button" onClick={() => onPrev()}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" disabled={!valid(passwordState)} onClick={() => onNext()}>次へ</button>
		</div>
	</>;
}
export function InputGenovisionInformation({ onNext, onPrev, genovisionTypeState }) {
	const passwordState = useValidateState('', v => v === '1971');
	return <>
		<h3>Genovision会員情報の入力</h3>
		<RadioContainer label="会員のタイプ">
			<Radio state={genovisionTypeState} value={1} name="chuei-bank-member-type">Genovision会員ご本人さま</Radio>
			<Radio state={genovisionTypeState} value={2} name="chuei-bank-member-type">ご紹介の方</Radio>
		</RadioContainer>
		<Text state={passwordState} label="お申し込みコードを入力してください​" name="genovision-code" />
		<div className="button-container">
			<button className="prev-button" onClick={() => onPrev()}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" disabled={!valid(passwordState, genovisionTypeState)} onClick={() => onNext()}>次へ</button>
		</div>
	</>;
}
export function InputCSPInformation({ onNext, onPrev, cspPromotionCodeState, testPlaces }: { testPlaces: PartialRequired<GQLTestPlace, 'id' | 'acceptPromotion' | 'clinicName' | 'name'>[]; } & { onNext: () => void; onPrev: () => void; cspPromotionCodeState: any; }) {
	return <>
		<h3>各種イベントで配布されたメタロ・バランス検査クーポンコードをお持ちの方</h3>
		<p className="entry-form-text">各種イベントで配布されたメタロ・バランス検査クーポンコードをお持ちの方は、がんリスク検査または認知リスク検査を通常19,800円<small>（税込）</small>のところ、特別料金17,600円<small>（税込）</small>にて受けていただけます。<br />
		がんリスク検査と認知リスク検査を同時に検査すると通常24,200円<small>（税込）</small>のところ、22,000円<small>（税込）</small>にて受けていただけます。</p>
		<Text state={cspPromotionCodeState} label="クーポンコード（英数6桁）をご入力ください。" replacer={toHalfString} name="csp-promotion-code" />
		<p className="entry-form-text"><br />なお、クーポンコードをお使いいただける検査会場は下記の通りです。</p>
		<div className="clinic-list">
			{testPlaces.filter(testPlace => testPlace.acceptPromotion).map(testPlace => {
				return <div key={testPlace.id} className="clinic-list-item">
					<a className="clinic-name" href={`/access/#l-${testPlace.id}`} target="_blank">
						{testPlace.name}
					</a>
				</div>;
			})}
		</div>
		<div className="button-container">
			<button className="prev-button" onClick={() => onPrev()}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" disabled={!valid(cspPromotionCodeState)} onClick={() => onNext()}>次へ</button>
		</div>
	</>;

}
export function InputMBClubMemberInformation({ onNext, onPrev, mbClubMemberIdState, lastTestLocationState, lastTestDateState, testPlaces }: NavigateProps & TestPlacesProps & { mbClubMemberIdState: any; lastTestLocationState: any; lastTestDateState: any; }) {
	const [] = mbClubMemberIdState;
	const [] = lastTestLocationState;
	const [lastTestDate] = lastTestDateState;
	const [year, setYear] = useState('');
	const [month, setMonth] = useState('');
	const [date, setDate] = useState('');
	const [blur, setBlur] = useState(false);

	useEffect(() => {
		const [, setLastTestDate] = lastTestDateState;
		const y = Number(year);
		const m = Number(month) - 1;
		const d = Number(date);
		const nd = new Date(1990, 0, 1);
		nd.setFullYear(y);
		nd.setMonth(m);
		nd.setDate(d);
		if (!Number.isNaN(nd.getTime()) && nd.getFullYear() === y && nd.getMonth() === m && nd.getDate() === d) {
			setLastTestDate(nd);
		} else {
			setLastTestDate(new Date(NaN));
		}
	}, [year, month, date]);

	return <>
		<h3>MBクラブ会員再検査申し込み</h3>
		<p className="entry-form-text">MBクラブ会員で年度内の再検査をご希望の方は、こちらからお申込みください</p>
		{/*-- <Text state={mbClubMemberIdState} label="MBクラブ会員番号（わからない場合は空欄）" /> --*/}
		<RadioContainer label="前回検査会場">
			{testPlaces.filter(p => p.acceptMbClub).map(p => {
				return <Radio key={p.id} state={lastTestLocationState} value={p.id} name="last-test-location">{p.name}</Radio>;
			})}
		</RadioContainer>
		<div className="input-container">
			<p className="label">前回検査（採血）日<small>(西暦)</small></p>
			<div className={classNames('date-input-container', { invalid: blur && !isValidDate(lastTestDate) })}>
				<label><input type="text" {...bindStateToText([year, setYear])} onBlur={() => {
					setBlur(true);
					setYear(removeNonNumericCharacters(year));
				}} />年</label>
				<label><input type="text" {...bindStateToText([month, setMonth])} onBlur={() => {
					setBlur(true);
					setMonth(removeNonNumericCharacters(month));
				}} />月</label>
				<label><input type="text" {...bindStateToText([date, setDate])} onBlur={() => {
					setBlur(true);
					setDate(removeNonNumericCharacters(date));
				}} />日</label>
			</div>
		</div>
		<p className="entry-form-text"> </p>
		<p className="entry-form-text">年度内再検査の方の検査料は7,027円（税込）です。<br />お支払いは、採血後の銀行口座振込となります。</p>
		<div className="button-container">
			<button className="prev-button" onClick={() => onPrev()}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" disabled={!valid(mbClubMemberIdState, lastTestLocationState, lastTestDateState)} onClick={() => onNext()}>次へ</button>
		</div>
	</>;
}

export function InputInsuranceInformation({ onNext, onPrev }) {
	const passwordState = useValidateState('', v => v === '27600'
	);
	return <>
		<h3>保険会社からご紹介情報の入力</h3>
		<Text state={passwordState} label="パスワード​" name="password" />
		<div className="button-container">
			<button className="prev-button" onClick={() => onPrev()}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" disabled={!valid(passwordState)} onClick={() => onNext()}>次へ</button>
		</div>
	</>;
}

export function InputCouponCodeInformation({ onNext, onPrev, couponCodeState }) {
	const codeState = useValidateState('', v => v.length > 0)
	const client = useApolloClient()
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const check = async() => {
		const [code] = codeState
		const {data, error} = await client.query<GQLCouponCodeQuery, GQLCouponCodeQueryVariables>({
			query: CouponCodeDocument,
			variables: {
				input: {
					code
				}
			},
			errorPolicy: 'ignore'
		})
		if(data?.couponCode){
			couponCodeState[1](data.couponCode)
			onNext()
		} else {
			setErrorMessage('有効なクーポンコードを入力してください')
		}
	}
	return <>
		<h3>メタロ・バランス検査 MBコード（クーポンコード）をお持ちの方</h3>
		<p className="entry-form-text"> MBコード（クーポンコード）をご入力ください</p>
		<Text state={codeState} label=" MBコード（クーポンコード）​" name="coupon-code" replacer={normalizeCouponCode} />
		{
			errorMessage && <p className="entry-form-text" style={{color: '#c82100'}}>{errorMessage}</p>
		}
		
		<div className="button-container">
			<button className="prev-button" onClick={() => onPrev()}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" disabled={!valid(couponCodeState)} onClick={check}>次へ</button>
		</div>
	</>;
}

export function InputGoldenLoopInformation({ onNext, onPrev, goldenLoopTypeState }) {
	const passwordState = useValidateState('', v => v === '1023');
	return <>
		<h3>ゴールデンループ読者、およびご紹介の方</h3>
		<RadioContainer label="会員のタイプ">
			<Radio state={goldenLoopTypeState} value={1} name="golden-loop-type">ゴールデンループ読者ご本人さま</Radio>
			<Radio state={goldenLoopTypeState} value={2} name="golden-loop-type">ご紹介の方</Radio>
		</RadioContainer>
		<Text state={passwordState} label="お申し込みコードを入力してください​" name="genovision-code" />
		<div className="button-container">
			<button className="prev-button" onClick={() => onPrev()}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" disabled={!valid(passwordState, goldenLoopTypeState)} onClick={() => onNext()}>次へ</button>
		</div>
	</>;
}
